import 'quill/dist/quill.snow.css'
import React, { useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import ReactQuill, { Quill } from 'react-quill-new'
import { Scope, StyleAttributor } from 'parchment'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { Button, Card } from '../components/ui'
import { API_BASE_URL, EmailCampaignFormMeta } from '../data'
import { EmailCampaign, EmailCampaignFormMetaProps, EmailCampaignRecipient, EmailCampaignRecipientSource } from '../data/types'
import { ChevronLeftIcon, DeleteIcon, EyeIcon, FlagIcon, PlusIcon, SendIcon, UsersIcon, XIcon } from '../icons'
import { useAuth } from './auth/AuthContext'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'


let Block = Quill.import('blots/block') as any;

class NoticeBlock extends Block {
    static blotName = 'notice';
    static tagName = 'div';
    static className = 'notice-block';

    static create(value: any) {
        let node = super.create();
        node.setAttribute('style', `background-color: #f0ede7; padding: 16px; border-radius: 8px; border-left: 4px solid #86744c;`);
        return node;
    }

    static formats(domNode: HTMLElement) {
        return domNode.getAttribute('style')?.includes('#f0ede7') ? true : false;
    }

    format(name: string, value: any) {
        if (name === NoticeBlock.blotName && !value) {
            // When toggling off, replace with a normal block
            this.replaceWith('block');
        } else {
            super.format(name, value);
        }
    }
}
Quill.register('formats/notice', NoticeBlock);

const AlignmentStyle = new StyleAttributor('align', 'text-align', { scope: Scope.BLOCK });
Quill.register(AlignmentStyle, true);

type Props = {
    meta: EmailCampaignFormMetaProps
}

const EmailCampaignForm = (props: Props) => {
    const { meta } = props
    const { campaignId } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditMode = !!campaignId
    const canDraftCampaigns = user?.permissions?.includes('manage_email_campaigns')
    const canApproveCampaigns = user?.permissions?.includes('approve_email_campaigns')

    const [subject, setSubject] = useState('')
    const [bodyHtml, setBodyHtml] = useState('')
    const [originalSubject, setOriginalSubject] = useState('')
    const [originalBodyHtml, setOriginalBodyHtml] = useState('')
    const [loading, setLoading] = useState(isEditMode)
    const [saving, setSaving] = useState(false)
    const [campaign, setCampaign] = useState<EmailCampaign | null>(null)
    const [showTestModal, setShowTestModal] = useState(false)
    const [showSendModal, setShowSendModal] = useState(false)
    const [showDenyModal, setShowDenyModal] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [previewHtml, setPreviewHtml] = useState('')
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [testEmails, setTestEmails] = useState<string[]>([''])
    const [recipientCount, setRecipientCount] = useState(0)
    const [sendingTest, setSendingTest] = useState(false)
    const [sendingCampaign, setSendingCampaign] = useState(false)
    const [denyingCampaign, setDenyingCampaign] = useState(false)
    const [showRetryModal, setShowRetryModal] = useState(false)
    const [retryingFailed, setRetryingFailed] = useState(false)

    // Targeting criteria state
    const [showTargetingModal, setShowTargetingModal] = useState(false)
    const [targetingMethod, setTargetingMethod] = useState<'all' | 'event' | 'duty' | 'leads'>('event')
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)
    const [selectedDutyId, setSelectedDutyId] = useState<number | null>(null)
    const [events, setEvents] = useState<any[]>([])
    const [areas, setAreas] = useState<any[]>([])
    const [shifts, setShifts] = useState<any[]>([])
    const [duties, setDuties] = useState<any[]>([])
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [loadingAreas, setLoadingAreas] = useState(false)
    const [loadingShifts, setLoadingShifts] = useState(false)
    const [loadingDuties, setLoadingDuties] = useState(false)
    const [includeEventLeads, setIncludeEventLeads] = useState(false)
    const [includeAreaLeads, setIncludeAreaLeads] = useState(false)
    const [includeShiftLeads, setIncludeShiftLeads] = useState(false)
    // Filters for "all volunteers" option
    const [volunteerTypes, setVolunteerTypes] = useState<('iiuk_uk_ireland' | 'iiuk_europe' | 'non_iiuk')[]>(['iiuk_uk_ireland'])
    const [volunteerForVisit, setVolunteerForVisit] = useState<'yes' | 'no' | 'both'>('yes')

    // Recipient management state
    const [recipients, setRecipients] = useState<EmailCampaignRecipient[]>([])
    const [loadingRecipients, setLoadingRecipients] = useState(false)
    const [showAddIndividualModal, setShowAddIndividualModal] = useState(false)
    const [showClearConfirmModal, setShowClearConfirmModal] = useState(false)
    const [volunteerSearchResults, setVolunteerSearchResults] = useState<any[]>([])
    const [searchingVolunteers, setSearchingVolunteers] = useState(false)
    const [searchPending, setSearchPending] = useState(false)
    const [selectedVolunteers, setSelectedVolunteers] = useState<string[]>([])
    const [addingRecipients, setAddingRecipients] = useState(false)
    const [showRecipientSourceHistory, setShowRecipientSourceHistory] = useState(false)

    // Recipient pagination state
    const [recipientPage, setRecipientPage] = useState(1)
    const [recipientTotalPages, setRecipientTotalPages] = useState(1)
    const [recipientTotal, setRecipientTotal] = useState(0)
    const [recipientWithEmailCount, setRecipientWithEmailCount] = useState(0)
    const [recipientPendingWithEmailCount, setRecipientPendingWithEmailCount] = useState(0)
    const RECIPIENTS_PER_PAGE = 20

    // Preview confirmation state
    const [showPreviewConfirmModal, setShowPreviewConfirmModal] = useState(false)
    const [previewData, setPreviewData] = useState<{ would_add: number; without_email?: number; duplicates: number; current_total: number; new_total: number } | null>(null)
    const [loadingPreviewData, setLoadingPreviewData] = useState(false)
    const [pendingTargetingBody, setPendingTargetingBody] = useState<any>(null)

    // Email suggestions modal state
    const [showEmailSuggestionsModal, setShowEmailSuggestionsModal] = useState(false)
    const [selectedRecipient, setSelectedRecipient] = useState<EmailCampaignRecipient | null>(null)
    const [emailSuggestions, setEmailSuggestions] = useState<{
        family_emails: { csid: string; name: string; email: string }[];
        previous_emails: string[];
        volunteer_email: string | null;
    } | null>(null)
    const [loadingEmailSuggestions, setLoadingEmailSuggestions] = useState(false)
    const [customEmail, setCustomEmail] = useState('')
    const [savingEmail, setSavingEmail] = useState(false)

    // Individual search fields
    const [nameSearch, setNameSearch] = useState('')
    const [emailSearch, setEmailSearch] = useState('')
    const [phoneSearch, setPhoneSearch] = useState('')
    const [idSearch, setIdSearch] = useState('')

    const quillRef = useRef<ReactQuill>(null)
    const searchDebounceRef = useRef<number | null>(null)

    useEffect(() => {
        if (isEditMode) {
            fetchCampaign()
        }
    }, [campaignId])

    useEffect(() => {
        // Add custom icon to notice button after Quill loads
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
            const noticeButton = toolbar.querySelector('.ql-notice');
            if (noticeButton && !noticeButton.hasAttribute('data-icon-rendered')) {
                // Clear any existing content
                noticeButton.innerHTML = '';
                // Render React icon component
                const root = createRoot(noticeButton);
                root.render(<FlagIcon className="w-[18px] h-[18px]" />);
                noticeButton.setAttribute('data-icon-rendered', 'true');
                noticeButton.setAttribute('title', 'Notice Block');
            }
        }
    }, [quillRef.current])

    const fetchCampaign = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaignId}`, {
                credentials: 'include'
            })

            if (!response.ok) throw new Error(meta.errorLoading)

            const result = await response.json()
            let data = result.value || result

            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            const campaignData = Array.isArray(data) ? data[0] : data
            setCampaign(campaignData)
            setSubject(campaignData.subject || '')
            setBodyHtml(campaignData.body_html || '')
            setOriginalSubject(campaignData.subject || '')
            setOriginalBodyHtml(campaignData.body_html || '')

            // Fetch recipients
            await fetchRecipients()
        } catch (err) {
            alert(err instanceof Error ? err.message : meta.errorLoading)
            navigate('/admin/email-campaigns')
        } finally {
            setLoading(false)
        }
    }

    const fetchRecipients = async (page: number = 1) => {
        if (!campaignId) return

        setLoadingRecipients(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaignId}/recipients?per_page=${RECIPIENTS_PER_PAGE}&page=${page}`, {
                credentials: 'include'
            })

            if (response.ok) {
                const result = await response.json()
                const paginatedData = result.value || result

                // Handle Laravel pagination response
                if (paginatedData.data) {
                    setRecipients(paginatedData.data)
                    setRecipientPage(paginatedData.current_page || 1)
                    setRecipientTotalPages(paginatedData.last_page || 1)
                    setRecipientTotal(paginatedData.total || 0)
                    setRecipientWithEmailCount(paginatedData.with_email_count || 0)
                    setRecipientPendingWithEmailCount(paginatedData.pending_with_email_count || 0)
                } else {
                    // Fallback for non-paginated response
                    let data = paginatedData
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setRecipients(Array.isArray(data) ? data : [])
                    setRecipientTotal(Array.isArray(data) ? data.length : 0)
                    // For non-paginated, count from the data
                    const dataArray = Array.isArray(data) ? data : []
                    setRecipientWithEmailCount(dataArray.filter((r: EmailCampaignRecipient) => r.email && r.email.trim() !== '').length)
                    setRecipientPendingWithEmailCount(dataArray.filter((r: EmailCampaignRecipient) => r.status === 'pending' && r.email && r.email.trim() !== '').length)
                }
            }
        } catch (err) {
            console.error('Failed to fetch recipients:', err)
        } finally {
            setLoadingRecipients(false)
        }
    }

    const handleSave = async () => {
        if (!subject.trim() || !bodyHtml.trim()) {
            alert('Please fill in all fields')
            return
        }

        setSaving(true)
        try {
            const url = isEditMode
                ? `${API_BASE_URL}/email-campaigns/${campaignId}`
                : `${API_BASE_URL}/email-campaigns`

            const method = isEditMode ? 'PUT' : 'POST'

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    subject,
                    body_html: bodyHtml
                })
            })

            if (!response.ok) throw new Error(meta.errorSaving)

            const result = await response.json()
            let data = result.value || result

            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            const savedCampaign = Array.isArray(data) ? data[0] : data
            setCampaign(savedCampaign)

            // Update original values after successful save
            setOriginalSubject(subject)
            setOriginalBodyHtml(bodyHtml)

            alert(meta.successSaved)

            if (!isEditMode) {
                navigate(`/admin/email-campaigns/${savedCampaign.id}/edit`)
            }
        } catch (err) {
            alert(err instanceof Error ? err.message : meta.errorSaving)
        } finally {
            setSaving(false)
        }
    }

    const handlePreview = async () => {
        setLoadingPreview(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    subject: subject || 'No Subject',
                    body_html: bodyHtml || '<p>No content yet...</p>'
                })
            })

            if (response.ok) {
                const html = await response.text()
                setPreviewHtml(html)
                setShowPreviewModal(true)
            } else {
                alert('Failed to load preview')
            }
        } catch (error) {
            console.error('Preview error:', error)
            alert('Failed to load preview')
        } finally {
            setLoadingPreview(false)
        }
    }

    const hasChanges = isEditMode && (subject !== originalSubject || bodyHtml !== originalBodyHtml)

    const handleDiscardChanges = () => {
        if (window.confirm('Are you sure you want to discard all changes?')) {
            setSubject(originalSubject)
            setBodyHtml(originalBodyHtml)
        }
    }

    const handleSendTest = async () => {
        const validEmails = testEmails.filter(email => email.trim() !== '' && email.includes('@'))

        if (validEmails.length === 0) {
            alert('Please enter at least one valid email address')
            return
        }

        if (!campaign) {
            alert('Please save the campaign first')
            return
        }

        setSendingTest(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ test_emails: validEmails })
            })

            if (!response.ok) throw new Error('Failed to send test emails')

            const result = await response.json()
            alert(meta.successTestSent)
            setShowTestModal(false)
            setTestEmails([''])

            // Refresh campaign data
            fetchCampaign()
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to send test emails')
        } finally {
            setSendingTest(false)
        }
    }

    const handlePrepareAndSubmitOrApprove = async () => {
        if (!campaign) return

        // Use the total count of recipients with valid emails (not just current page)
        // For continue sending, use pending with email count
        const validEmailCount = campaign.started_sending_at
            ? recipientPendingWithEmailCount
            : recipientWithEmailCount

        // Show send confirmation modal if there are recipients with valid emails
        if (validEmailCount > 0) {
            setRecipientCount(validEmailCount)
            setShowSendModal(true)
        } else if (recipientTotal > 0) {
            alert('No recipients have valid email addresses. Please add emails before sending.')
        } else {
            alert('Please add recipients before sending the campaign.')
        }
    }

    const handleRetryFailed = async () => {
        if (!campaign) return

        setRetryingFailed(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/retry-failed`, {
                method: 'POST',
                credentials: 'include'
            })

            if (!response.ok) {
                const error = await response.json()
                throw new Error(error.error || 'Failed to retry failed emails')
            }

            const result = await response.json()
            const data = result.value || result

            alert(meta.successRetry(data.retried_count))
            setShowRetryModal(false)

            // Refresh campaign data
            fetchCampaign()
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to retry failed emails')
        } finally {
            setRetryingFailed(false)
        }
    }

    const handleAddGroup = () => {
        // Reset targeting selections
        setSelectedEventId(null)
        setSelectedAreaId(null)
        setSelectedShiftId(null)
        setSelectedDutyId(null)
        setIncludeEventLeads(false)
        setIncludeAreaLeads(false)
        setIncludeShiftLeads(false)
        setVolunteerTypes(['iiuk_uk_ireland', 'iiuk_europe', 'non_iiuk'])
        setVolunteerForVisit('both')
        setTargetingMethod('event')
        setShowTargetingModal(true)
    }

    // Build targeting body based on current selections
    const buildTargetingBody = () => {
        const body: any = {}

        if (targetingMethod === 'all') {
            body.all_volunteers = true
            // Add filters for "all volunteers" mode
            if (volunteerTypes.length > 0 && volunteerTypes.length < 3) {
                body.volunteer_types = volunteerTypes
            }
            if (volunteerForVisit !== 'both') {
                body.volunteer_for_visit = volunteerForVisit
            }
        } else if (targetingMethod === 'event') {
            if (selectedShiftId) {
                body.shift_id = selectedShiftId
            } else if (selectedAreaId) {
                body.area_id = selectedAreaId
            } else if (selectedEventId) {
                body.event_id = selectedEventId
            } else {
                body.all_allocated = true
            }
            if (includeEventLeads) body.include_event_leads = true
            if (includeAreaLeads) body.include_area_leads = true
            if (includeShiftLeads) body.include_shift_leads = true
        } else if (targetingMethod === 'duty') {
            if (selectedDutyId) {
                body.duty_id = selectedDutyId
            }
            if (includeShiftLeads) body.include_shift_leads = true
        } else if (targetingMethod === 'leads') {
            if (includeEventLeads) body.include_event_leads = true
            if (includeAreaLeads) body.include_area_leads = true
            if (includeShiftLeads) body.include_shift_leads = true
        }

        return body
    }

    // Preview the recipients before adding (first step)
    const handlePreviewGroupTargeting = async () => {
        if (!campaign) return

        setLoadingPreviewData(true)
        try {
            const body = buildTargetingBody()

            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            })

            if (!response.ok) throw new Error('Failed to preview recipients')

            const result = await response.json()
            const data = result.value || result

            setPreviewData(data)
            setPendingTargetingBody(body)
            setShowTargetingModal(false)
            setShowPreviewConfirmModal(true)
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to preview recipients')
        } finally {
            setLoadingPreviewData(false)
        }
    }

    // Actually add the recipients after confirmation (second step)
    const handleConfirmAddGroup = async () => {
        if (!campaign || !pendingTargetingBody) return

        setAddingRecipients(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(pendingTargetingBody)
            })

            if (!response.ok) throw new Error('Failed to add recipients')

            const result = await response.json()
            const data = result.value || result
            appendRecipientSource(data.recipient_source)

            setShowPreviewConfirmModal(false)
            setPreviewData(null)
            setPendingTargetingBody(null)
            await fetchRecipients()
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to add recipients')
        } finally {
            setAddingRecipients(false)
        }
    }

    const handleAddGroupWithTargeting = async () => {
        if (!campaign) return

        setAddingRecipients(true)
        try {
            // Build request body based on targeting method
            const body: any = {}

            if (targetingMethod === 'all') {
                // All registered volunteers - send empty body with special flag
                body.all_volunteers = true
                // Add filters for "all volunteers" mode
                if (volunteerTypes.length > 0 && volunteerTypes.length < 3) {
                    body.volunteer_types = volunteerTypes
                }
                if (volunteerForVisit !== 'both') {
                    body.volunteer_for_visit = volunteerForVisit
                }
            } else if (targetingMethod === 'event') {
                // Event/Area/Shift targeting - requires at least an event
                // If no event selected, target all volunteers with any allocation
                if (selectedShiftId) {
                    body.shift_id = selectedShiftId
                } else if (selectedAreaId) {
                    body.area_id = selectedAreaId
                } else if (selectedEventId) {
                    body.event_id = selectedEventId
                } else {
                    // All allocated volunteers (any event)
                    body.all_allocated = true
                }
                // Add lead flags if any are selected
                if (includeEventLeads) body.include_event_leads = true
                if (includeAreaLeads) body.include_area_leads = true
                if (includeShiftLeads) body.include_shift_leads = true
            } else if (targetingMethod === 'duty') {
                // Duty-based targeting
                if (selectedDutyId) {
                    body.duty_id = selectedDutyId
                }
                // Add shift leads flag if selected
                if (includeShiftLeads) body.include_shift_leads = true
            } else if (targetingMethod === 'leads') {
                // Lead-only targeting
                if (includeEventLeads) body.include_event_leads = true
                if (includeAreaLeads) body.include_area_leads = true
                if (includeShiftLeads) body.include_shift_leads = true
            }

            // Add recipients (incremental, not replacing)
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(body)
            })

            if (!response.ok) throw new Error('Failed to add recipients')

            const result = await response.json()
            const data = result.value || result

            appendRecipientSource(data.recipient_source)

            alert(meta.groupAddedMessage(data.added_count, data.total_recipients))
            setShowTargetingModal(false)
            await fetchRecipients()
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to add recipients')
        } finally {
            setAddingRecipients(false)
        }
    }

    const handleRemoveRecipient = async (recipientId: number) => {
        if (!campaign) return

        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/${recipientId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to remove recipient')

            // Refresh current page to get updated list
            await fetchRecipients(recipientPage)
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to remove recipient')
        }
    }

    const handleClearAllRecipients = async () => {
        if (!campaign) return

        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to clear recipients')

            const result = await response.json()
            const data = result.value || result
            appendRecipientSource(data.recipient_source)

            setRecipients([])
            setRecipientTotal(0)
            setRecipientWithEmailCount(0)
            setRecipientPendingWithEmailCount(0)
            setShowClearConfirmModal(false)
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to clear recipients')
        }
    }

    // Check if recipient has a valid email
    const recipientHasEmail = (recipient: EmailCampaignRecipient) => {
        return recipient.email && recipient.email.trim() !== ''
    }

    const appendRecipientSource = (recipientSource?: EmailCampaignRecipientSource) => {
        if (!recipientSource) return

        setCampaign(prev => prev
            ? { ...prev, recipient_sources: [recipientSource, ...(prev.recipient_sources || [])] }
            : prev
        )
    }

    const formatRecipientSourceDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString()
    }

    // Check if campaign content can be edited (before sending starts)
    const canEditContent = () => {
        return !!canDraftCampaigns && (!campaign || campaign.status === 'draft' || campaign.status === 'test_sent')
    }

    // Check if recipients can still be managed
    const canManageRecipients = () => {
        return !!canDraftCampaigns && (campaign?.status === 'draft' || campaign?.status === 'test_sent')
    }

    // Open email suggestions modal for a recipient without email
    const handleOpenEmailSuggestions = async (recipient: EmailCampaignRecipient) => {
        if (!campaign) return

        setSelectedRecipient(recipient)
        setShowEmailSuggestionsModal(true)
        setCustomEmail('')
        setEmailSuggestions(null)
        setLoadingEmailSuggestions(true)

        try {
            const response = await fetch(
                `${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/${recipient.volunteer_id}/email-suggestions`,
                { credentials: 'include' }
            )

            if (!response.ok) throw new Error('Failed to fetch email suggestions')

            const result = await response.json()
            setEmailSuggestions(result.value)
        } catch (err) {
            console.error('Failed to fetch email suggestions:', err)
            setEmailSuggestions({ family_emails: [], previous_emails: [], volunteer_email: null })
        } finally {
            setLoadingEmailSuggestions(false)
        }
    }

    // Save selected/custom email for recipient
    const handleSaveRecipientEmail = async (email: string) => {
        if (!campaign || !selectedRecipient) return

        setSavingEmail(true)
        try {
            const response = await fetch(
                `${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/${selectedRecipient.id}/email`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ email })
                }
            )

            if (!response.ok) {
                const error = await response.json()
                throw new Error(error.error || 'Failed to update email')
            }

            // Update the recipient in local state
            setRecipients(prev => prev.map(r =>
                r.id === selectedRecipient.id ? { ...r, email } : r
            ))

            setShowEmailSuggestionsModal(false)
            setSelectedRecipient(null)
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update email')
        } finally {
            setSavingEmail(false)
        }
    }

    // Check if any search field has a value
    const hasSearchCriteria = () => {
        return nameSearch.length >= 2 || emailSearch.length >= 2 || phoneSearch.length >= 2 || idSearch.length >= 1
    }

    // Search volunteers for individual add (with debounce)
    const performVolunteerSearch = async () => {
        if (!hasSearchCriteria()) {
            setVolunteerSearchResults([])
            return
        }

        setSearchingVolunteers(true)
        try {
            // Build query params from all search fields
            const params = new URLSearchParams()
            params.append('per_page', '20')
            if (nameSearch.trim()) params.append('name', nameSearch.trim())
            if (emailSearch.trim()) params.append('email', emailSearch.trim())
            if (phoneSearch.trim()) params.append('phone', phoneSearch.trim())
            if (idSearch.trim()) params.append('id', idSearch.trim())

            const response = await fetch(`${API_BASE_URL}/volunteer?${params.toString()}`, {
                credentials: 'include'
            })

            if (response.ok) {
                const result = await response.json()
                // The volunteer API returns paginated data with a 'data' property
                let data = result.data || result.value || result
                if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                    data = data[0]
                }
                // Filter out volunteers already in recipients and those without email
                const existingCsids = recipients.map(r => r.volunteer_id)
                const filtered = (Array.isArray(data) ? data : []).filter(
                    (v: any) => !existingCsids.includes(v.csid) && v.email
                )
                setVolunteerSearchResults(filtered)
            }
        } catch (err) {
            console.error('Failed to search volunteers:', err)
        } finally {
            setSearchingVolunteers(false)
        }
    }

    // Debounced search trigger
    const triggerDebouncedSearch = () => {
        if (searchDebounceRef.current) {
            window.clearTimeout(searchDebounceRef.current)
        }
        // Show searching state immediately if criteria is met
        if (hasSearchCriteria()) {
            setSearchPending(true)
        }
        searchDebounceRef.current = window.setTimeout(() => {
            setSearchPending(false)
            performVolunteerSearch()
        }, 300)
    }

    // Effect to trigger search when any search field changes
    useEffect(() => {
        triggerDebouncedSearch()
        return () => {
            if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current)
            }
        }
    }, [nameSearch, emailSearch, phoneSearch, idSearch])

    const handleAddIndividualRecipients = async () => {
        if (!campaign || selectedVolunteers.length === 0) return

        setAddingRecipients(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/recipients/add-individual`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ volunteer_csids: selectedVolunteers })
            })

            if (!response.ok) throw new Error('Failed to add recipients')

            const result = await response.json()
            const data = result.value || result

            appendRecipientSource(data.recipient_source)

            alert(meta.individualAddedMessage(data.added_count, data.total_recipients))
            setShowAddIndividualModal(false)
            setNameSearch('')
            setEmailSearch('')
            setPhoneSearch('')
            setIdSearch('')
            setVolunteerSearchResults([])
            setSelectedVolunteers([])
            await fetchRecipients()
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to add recipients')
        } finally {
            setAddingRecipients(false)
        }
    }

    // Fetch events for targeting
    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true)
            try {
                const response = await fetch(`${API_BASE_URL}/event`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setEvents(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch events:', err)
            } finally {
                setLoadingEvents(false)
            }
        }
        fetchEvents()
    }, [])

    // Fetch duties for targeting
    useEffect(() => {
        const fetchDuties = async () => {
            setLoadingDuties(true)
            try {
                const response = await fetch(`${API_BASE_URL}/duty`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setDuties(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch duties:', err)
            } finally {
                setLoadingDuties(false)
            }
        }
        fetchDuties()
    }, [])

    // Fetch areas when event is selected
    useEffect(() => {
        if (!selectedEventId) {
            setAreas([])
            setSelectedAreaId(null)
            return
        }

        const fetchAreas = async () => {
            setLoadingAreas(true)
            try {
                const response = await fetch(`${API_BASE_URL}/area/${selectedEventId}`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setAreas(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch areas:', err)
            } finally {
                setLoadingAreas(false)
            }
        }
        fetchAreas()
    }, [selectedEventId])

    // Fetch shifts when area is selected
    useEffect(() => {
        if (!selectedAreaId) {
            setShifts([])
            setSelectedShiftId(null)
            return
        }

        const fetchShifts = async () => {
            setLoadingShifts(true)
            try {
                const response = await fetch(`${API_BASE_URL}/shift/${selectedAreaId}`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const result = await response.json()
                    let data = result.value || result
                    if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                        data = data[0]
                    }
                    setShifts(Array.isArray(data) ? data : [])
                }
            } catch (err) {
                console.error('Failed to fetch shifts:', err)
            } finally {
                setLoadingShifts(false)
            }
        }
        fetchShifts()
    }, [selectedAreaId])

    const handleConfirmSend = async () => {
        if (!campaign) return

        setSendingCampaign(true)
        try {
            const isPendingApproval = campaign.status === 'pending_approval'
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/${isPendingApproval ? 'approve' : 'submit-for-approval'}`, {
                method: 'POST',
                credentials: 'include'
            })

            if (!response.ok) throw new Error(isPendingApproval ? 'Failed to approve campaign' : 'Failed to submit campaign for approval')

            alert(isPendingApproval ? meta.successSent : meta.successSubmitted)
            navigate('/admin/email-campaigns')
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update campaign')
        } finally {
            setSendingCampaign(false)
            setShowSendModal(false)
        }
    }

    const handleConfirmDeny = async () => {
        if (!campaign) return

        setDenyingCampaign(true)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaign.id}/deny`, {
                method: 'POST',
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to deny campaign')

            alert(meta.successDenied)
            navigate('/admin/email-campaigns')
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to deny campaign')
        } finally {
            setDenyingCampaign(false)
            setShowDenyModal(false)
        }
    }

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['notice'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link'],
            ['clean'],
        ]
    }

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'color', 'background',
        'align',
        'link',
        'notice'
    ]

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8 max-w-5xl">
                <LoadingSpinner meta={{ loadingText: meta.editTitle }} />
            </div>
        )
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            {/* Header */}
            <div className="mb-6">
                <Button
                    onClick={() => navigate('/admin/email-campaigns')}
                    variant="icon"
                    color="neutral"
                    className="mb-4"
                >
                    <ChevronLeftIcon className="w-5 h-5" />
                    Back to Campaigns
                </Button>

                <h1 className="text-3xl font-bold text-stone-800 dark:text-stone-100">
                    {isEditMode ? meta.editTitle : meta.createTitle}
                </h1>
            </div>

            <Card>
                <div className="p-6 space-y-6">
                    {/* Content locked notice */}
                    {campaign && (campaign.status === 'pending_approval' || campaign.started_sending_at || campaign.status === 'completed' || campaign.status === 'failed') && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                {campaign.status === 'pending_approval' ? meta.contentLockedPendingApproval : 'Email content is locked after approval or sending has started.'}
                            </p>
                        </div>
                    )}

                    {/* Subject Field */}
                    <div>
                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                            {meta.subjectLabel}
                        </label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder={meta.subjectPlaceholder}
                            disabled={!canEditContent()}
                            className={`w-full px-4 py-2 border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100 ${!canEditContent() ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                    </div>

                    {/* Body Field */}
                    <div>
                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                            {meta.bodyLabel}
                        </label>
                        <div className={`bg-white dark:bg-stone-800 rounded-lg ${!canEditContent() ? 'opacity-60 pointer-events-none' : ''}`}>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={bodyHtml}
                                onChange={setBodyHtml}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder={meta.bodyPlaceholder}
                                className="email-editor"
                                readOnly={!canEditContent()}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-200 dark:border-stone-700">
                        {/* Save Draft and Discard - only show if content can be edited and has changes */}
                        {canEditContent() && (!isEditMode || hasChanges) && (
                            <>
                                <Button
                                    onClick={handleSave}
                                    disabled={saving}
                                    variant="solid"
                                    color="neutral"
                                >
                                    {saving ? 'Saving...' : meta.saveDraftButton}
                                </Button>

                                {isEditMode && hasChanges && (
                                    <Button
                                        onClick={handleDiscardChanges}
                                        variant="outline"
                                        color="neutral"
                                    >
                                        Discard Changes
                                    </Button>
                                )}
                            </>
                        )}

                        <Button
                            onClick={handlePreview}
                            disabled={loadingPreview}
                            variant="outline"
                            color="neutral"
                        >
                            <EyeIcon className="w-5 h-5" />
                            {loadingPreview ? 'Loading...' : meta.previewButton}
                        </Button>

                        {/* Test email button - only before sending starts */}
                        {campaign && canEditContent() && (
                            <>
                                <Button
                                    onClick={() => setShowTestModal(true)}
                                    variant="outline"
                                    color="primary"
                                >
                                    <SendIcon className="w-5 h-5" />
                                    {meta.sendTestButton}
                                </Button>
                            </>
                        )}

                        {/* Cancel button - only show when creating new campaign */}
                        {!isEditMode && (
                            <Button
                                onClick={() => navigate('/admin/email-campaigns')}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                        )}
                    </div>

                    {/* Recipient Management Section - show for saved campaigns that aren't completed/failed */}
                    {campaign && (campaign.status === 'draft' || campaign.status === 'test_sent' || campaign.status === 'pending_approval' || campaign.status === 'sending') && (
                        <div className="pt-6 border-t border-stone-200 dark:border-stone-700">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100 flex items-center gap-2">
                                        <UsersIcon className="w-5 h-5" />
                                        {meta.recipientManagementTitle}
                                    </h3>
                                    <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                                        {meta.recipientManagementDescription}
                                    </p>
                                </div>
                                <div className="text-end">
                                    <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                        {recipientTotal}
                                    </span>
                                    <span className="text-sm text-stone-600 dark:text-stone-400 ms-1">
                                        {recipientTotal === 1 ? 'recipient' : 'recipients'}
                                    </span>
                                </div>
                            </div>

                            {canApproveCampaigns && (
                                <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50/60 p-4 dark:border-primary-800 dark:bg-primary-900/20">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-sm font-semibold text-stone-800 dark:text-stone-100">
                                                {meta.recipientSourceHistoryTitle}
                                            </h4>
                                            <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                                                {meta.recipientSourceHistoryDescription}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={() => setShowRecipientSourceHistory(current => !current)}
                                            variant="outline"
                                            color="primary"
                                            size="sm"
                                        >
                                            {showRecipientSourceHistory ? meta.recipientSourceHideButton : meta.recipientSourceShowButton}
                                        </Button>
                                    </div>

                                    {showRecipientSourceHistory && (
                                        <div className="mt-3">
                                            {(campaign.recipient_sources || []).length === 0 ? (
                                                <p className="text-sm text-stone-600 dark:text-stone-400">
                                                    {meta.recipientSourceNoHistory}
                                                </p>
                                            ) : (
                                                <div className="space-y-3">
                                                    {(campaign.recipient_sources || []).map(source => (
                                                        <div key={source.id} className="rounded-md bg-white p-3 shadow-sm dark:bg-stone-900">
                                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                                <div>
                                                                    <p className="text-sm font-medium text-stone-800 dark:text-stone-100">
                                                                        {source.summary}
                                                                    </p>
                                                                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
                                                                        {formatRecipientSourceDate(source.created_at)}
                                                                        {source.creator && ` · ${meta.recipientSourceAddedByLabel} ${source.creator.name || source.creator.email}`}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 grid gap-2 text-xs text-stone-600 dark:text-stone-300 sm:grid-cols-2 lg:grid-cols-5">
                                                                <span>{meta.recipientSourceSelectedLabel}: <strong>{source.selected_count}</strong></span>
                                                                <span>{meta.recipientSourceAddedLabel}: <strong>{source.added_count}</strong></span>
                                                                <span>{meta.recipientSourceDuplicatesLabel}: <strong>{source.duplicate_count}</strong></span>
                                                                <span>{meta.recipientSourceWithoutEmailLabel}: <strong>{source.without_email_count}</strong></span>
                                                                <span>{meta.recipientSourceTotalAfterLabel}: <strong>{source.total_recipients_after}</strong></span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Add Recipients Buttons - only before sending starts */}
                            {canManageRecipients() && (
                            <div className="flex flex-wrap gap-3 mb-4">
                                <Button
                                    onClick={handleAddGroup}
                                    variant="outline"
                                    color="primary"
                                >
                                    <UsersIcon className="w-5 h-5" />
                                    {meta.addGroupButton}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setNameSearch('')
                                        setEmailSearch('')
                                        setPhoneSearch('')
                                        setIdSearch('')
                                        setVolunteerSearchResults([])
                                        setSelectedVolunteers([])
                                        setShowAddIndividualModal(true)
                                    }}
                                    variant="outline"
                                    color="primary"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    {meta.addIndividualButton}
                                </Button>
                                {/* Clear All - only before sending starts */}
                                {recipients.length > 0 && (
                                    <Button
                                        onClick={() => setShowClearConfirmModal(true)}
                                        variant="outline"
                                        color="danger"
                                    >
                                        <DeleteIcon className="w-5 h-5" />
                                        {meta.clearAllButton}
                                    </Button>
                                )}
                            </div>
                            )}

                            {/* Warning for recipients without emails */}
                            {(recipientTotal - recipientWithEmailCount) > 0 && (
                                <div className="mb-4 p-3 bg-warn-50 dark:bg-warn-900/30 border border-warn-300 dark:border-warn-700 rounded-lg">
                                    <div className="flex items-center gap-2 text-warn-800 dark:text-warn-200">
                                        <span className="text-warn-500">⚠</span>
                                        <span className="font-medium">
                                            {recipientTotal - recipientWithEmailCount} recipient{(recipientTotal - recipientWithEmailCount) !== 1 ? 's' : ''} missing email address
                                        </span>
                                    </div>
                                    <p className="text-sm text-warn-700 dark:text-warn-300 mt-1">
                                        These recipients are shown at the top of the list. Click "No email" to add an email address.
                                    </p>
                                </div>
                            )}

                            {/* Recipient List */}
                            <div className="border border-stone-200 dark:border-stone-700 rounded-lg overflow-hidden">
                                {loadingRecipients ? (
                                    <div className="p-4 text-center text-stone-500 dark:text-stone-400">
                                        Loading recipients...
                                    </div>
                                ) : recipients.length === 0 ? (
                                    <div className="p-8 text-center text-stone-500 dark:text-stone-400">
                                        <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p>{meta.noRecipientsMessage}</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="max-h-64 overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-stone-100 dark:bg-stone-800 sticky top-0">
                                                    <tr>
                                                        <th className="text-start px-4 py-2 font-medium text-stone-700 dark:text-stone-300">Name</th>
                                                        <th className="text-start px-4 py-2 font-medium text-stone-700 dark:text-stone-300">Email</th>
                                                        <th className="text-start px-4 py-2 font-medium text-stone-700 dark:text-stone-300">Status</th>
                                                        <th className="text-end px-4 py-2 font-medium text-stone-700 dark:text-stone-300">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-stone-200 dark:divide-stone-700">
                                                    {recipients.map(recipient => (
                                                        <tr key={recipient.id} className={`hover:bg-stone-50 dark:hover:bg-stone-800/50 ${recipient.status !== 'pending' ? 'bg-stone-50 dark:bg-stone-800/30' : ''}`}>
                                                            <td className="px-4 py-2 text-stone-800 dark:text-stone-200">
                                                                {recipient.volunteer
                                                                    ? `${recipient.volunteer.first_name} ${recipient.volunteer.last_name}`
                                                                    : recipient.volunteer_id}
                                                            </td>
                                                            <td className="px-4 py-2 text-stone-600 dark:text-stone-400">
                                                                {recipientHasEmail(recipient) ? (
                                                                    recipient.email
                                                                ) : (canManageRecipients() && recipient.status === 'pending') ? (
                                                                    <button
                                                                        onClick={() => handleOpenEmailSuggestions(recipient)}
                                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-warn-100 text-warn-800 dark:bg-warn-900/50 dark:text-warn-300 hover:bg-warn-200 dark:hover:bg-warn-900/70 transition-colors"
                                                                    >
                                                                        <span className="text-warn-500">⚠</span>
                                                                        No email - Click to add
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-stone-400 dark:text-stone-500 italic">No email</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2">
                                                                {recipient.status === 'sent' && (
                                                                    <span
                                                                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-800 dark:bg-success-900/50 dark:text-success-300"
                                                                        title={recipient.sent_at ? `Sent at ${new Date(recipient.sent_at).toLocaleString()}` : 'Sent'}
                                                                    >
                                                                        Sent
                                                                    </span>
                                                                )}
                                                                {recipient.status === 'failed' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900/50 dark:text-danger-300" title={recipient.error_message || 'Failed to send'}>
                                                                        Failed
                                                                    </span>
                                                                )}
                                                                {recipient.status === 'sending' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-300">
                                                                        Sending...
                                                                    </span>
                                                                )}
                                                                {recipient.status === 'pending' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-stone-100 text-stone-600 dark:bg-stone-700 dark:text-stone-300">
                                                                        Pending
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 text-end">
                                                                {canManageRecipients() && recipient.status === 'pending' ? (
                                                                    <button
                                                                        onClick={() => handleRemoveRecipient(recipient.id)}
                                                                        className="text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
                                                                        title="Remove recipient"
                                                                    >
                                                                        <XIcon className="w-4 h-4" />
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-stone-300 dark:text-stone-600">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination controls */}
                                        {recipientTotalPages > 1 && (
                                            <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 dark:border-stone-700">
                                                <div className="text-sm text-stone-600 dark:text-stone-400">
                                                    Showing {((recipientPage - 1) * RECIPIENTS_PER_PAGE) + 1} - {Math.min(recipientPage * RECIPIENTS_PER_PAGE, recipientTotal)} of {recipientTotal}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={() => fetchRecipients(recipientPage - 1)}
                                                        disabled={recipientPage <= 1 || loadingRecipients}
                                                        variant="outline"
                                                        color="neutral"
                                                        size="sm"
                                                    >
                                                        Previous
                                                    </Button>
                                                    <span className="px-3 py-1 text-sm text-stone-600 dark:text-stone-400">
                                                        Page {recipientPage} of {recipientTotalPages}
                                                    </span>
                                                    <Button
                                                        onClick={() => fetchRecipients(recipientPage + 1)}
                                                        disabled={recipientPage >= recipientTotalPages || loadingRecipients}
                                                        variant="outline"
                                                        color="neutral"
                                                        size="sm"
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Send Campaign Button - show if we can manage recipients */}
                            {((canDraftCampaigns && (campaign.status === 'draft' || campaign.status === 'test_sent')) || (canApproveCampaigns && campaign.status === 'pending_approval')) && (
                                <div className="flex justify-end gap-3 mt-4">
                                    {canApproveCampaigns && campaign.status === 'pending_approval' && (
                                        <Button
                                            onClick={() => setShowDenyModal(true)}
                                            disabled={denyingCampaign || sendingCampaign}
                                            variant="outline"
                                            color="danger"
                                            size="lg"
                                        >
                                            <XIcon className="w-5 h-5" />
                                            {meta.denyCampaignButton}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={handlePrepareAndSubmitOrApprove}
                                        disabled={sendingCampaign || (campaign?.started_sending_at ? recipientPendingWithEmailCount === 0 : recipientWithEmailCount === 0)}
                                        variant="solid"
                                        color="primary"
                                        size="lg"
                                    >
                                        <SendIcon className="w-5 h-5" />
                                        {campaign.status === 'pending_approval' ? meta.approveCampaignButton : meta.submitForApprovalButton} ({campaign.status === 'pending_approval' ? recipientPendingWithEmailCount : recipientWithEmailCount})
                                    </Button>
                                </div>
                            )}

                        </div>
                    )}

                    {/* Retry Failed Button - show for completed/failed campaigns with failed recipients */}
                    {campaign && (campaign.status === 'completed' || campaign.status === 'failed') && campaign.failed_count > 0 && (
                        <div className="flex justify-end pt-6 border-t border-stone-200 dark:border-stone-700">
                            <Button
                                onClick={() => setShowRetryModal(true)}
                                disabled={retryingFailed}
                                variant="solid"
                                color="warn"
                                size="lg"
                            >
                                <SendIcon className="w-5 h-5" />
                                {meta.retryFailedButton} ({campaign.failed_count})
                            </Button>
                        </div>
                    )}
                </div>
            </Card>

            {/* Test Email Modal */}
            {showTestModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <Card className="max-w-md w-full">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-4">
                                {meta.sendTestButton}
                            </h3>

                            <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
                                {meta.testEmailsHint}
                            </p>

                            <div className="space-y-3 mb-6">
                                {testEmails.map((email, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                                const newEmails = [...testEmails]
                                                newEmails[index] = e.target.value
                                                setTestEmails(newEmails)
                                            }}
                                            placeholder={meta.testEmailsPlaceholder}
                                            className="flex-1 px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100"
                                        />
                                        {testEmails.length > 1 && (
                                            <Button
                                                onClick={() => setTestEmails(testEmails.filter((_, i) => i !== index))}
                                                variant="outline"
                                                color="danger"
                                            >
                                                {meta.removeEmailButton}
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            {testEmails.length < 10 && (
                                <Button
                                    onClick={() => setTestEmails([...testEmails, ''])}
                                    variant="icon"
                                    color="primary"
                                    size="sm"
                                >
                                    + {meta.addEmailButton}
                                </Button>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => {
                                    setShowTestModal(false)
                                    setTestEmails([''])
                                }}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleSendTest}
                                disabled={sendingTest}
                                variant="solid"
                                color="primary"
                            >
                                {sendingTest ? 'Sending...' : meta.sendTestButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Add Individual Recipients Modal */}
        {showAddIndividualModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                    <div className="p-6 flex-shrink-0">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                            {meta.addIndividualButton}
                        </h3>
                        <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
                            Search for volunteers to add as recipients.
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <input
                                    type="text"
                                    value={nameSearch}
                                    onChange={(e) => setNameSearch(e.target.value)}
                                    placeholder="Search by name..."
                                    className="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={emailSearch}
                                    onChange={(e) => setEmailSearch(e.target.value)}
                                    placeholder="Search by email..."
                                    className="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={phoneSearch}
                                    onChange={(e) => setPhoneSearch(e.target.value)}
                                    placeholder="Search by phone..."
                                    className="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100"
                                />
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={idSearch}
                                    onChange={(e) => setIdSearch(e.target.value)}
                                    placeholder="Search by ID..."
                                    className="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-stone-800 dark:text-stone-100"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 border-t border-stone-200 dark:border-stone-700">
                        {searchingVolunteers || searchPending ? (
                            <div className="py-8 text-center text-stone-500 dark:text-stone-400">
                                Searching...
                            </div>
                        ) : !hasSearchCriteria() ? (
                            <div className="py-8 text-center text-stone-500 dark:text-stone-400">
                                Enter search criteria above (min 2 characters for name/email/phone, 1 for ID)
                            </div>
                        ) : volunteerSearchResults.length === 0 ? (
                            <div className="py-8 text-center text-stone-500 dark:text-stone-400">
                                No volunteers found
                            </div>
                        ) : (
                            <div className="py-2 divide-y divide-stone-200 dark:divide-stone-700">
                                {volunteerSearchResults.map(volunteer => (
                                    <label
                                        key={volunteer.csid}
                                        className="flex items-center gap-3 py-3 cursor-pointer hover:bg-stone-50 dark:hover:bg-stone-800/50"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedVolunteers.includes(String(volunteer.csid))}
                                            onChange={(e) => {
                                                const csidStr = String(volunteer.csid)
                                                if (e.target.checked) {
                                                    setSelectedVolunteers(prev => [...prev, csidStr])
                                                } else {
                                                    setSelectedVolunteers(prev => prev.filter(c => c !== csidStr))
                                                }
                                            }}
                                            className="rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div className="flex-1">
                                            <div className="font-medium text-stone-800 dark:text-stone-200">
                                                {volunteer.first_name} {volunteer.last_name}
                                            </div>
                                            <div className="text-sm text-stone-500 dark:text-stone-400">
                                                {volunteer.email}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="p-6 flex-shrink-0 border-t border-stone-200 dark:border-stone-700 flex gap-3 justify-between items-center">
                        <div className="text-sm text-stone-600 dark:text-stone-400">
                            {selectedVolunteers.length > 0 && `${selectedVolunteers.length} selected`}
                        </div>
                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAddIndividualModal(false)
                                    setNameSearch('')
                                    setEmailSearch('')
                                    setPhoneSearch('')
                                    setIdSearch('')
                                    setVolunteerSearchResults([])
                                    setSelectedVolunteers([])
                                }}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleAddIndividualRecipients}
                                disabled={addingRecipients || selectedVolunteers.length === 0}
                                variant="solid"
                                color="primary"
                            >
                                <PlusIcon className="w-5 h-5" />
                                {addingRecipients ? 'Adding...' : meta.addSelectedButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Clear All Confirmation Modal */}
        {showClearConfirmModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-4">
                            {meta.clearConfirmTitle}
                        </h3>
                        <p className="text-stone-600 dark:text-stone-400 mb-6">
                            {meta.clearConfirmMessage}
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => setShowClearConfirmModal(false)}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleClearAllRecipients}
                                variant="solid"
                                color="danger"
                            >
                                <DeleteIcon className="w-5 h-5" />
                                {meta.confirmClearButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Email Suggestions Modal */}
        {showEmailSuggestionsModal && selectedRecipient && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100">
                                {meta.emailSuggestionsTitle || 'Set Email Address'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowEmailSuggestionsModal(false)
                                    setSelectedRecipient(null)
                                }}
                                className="text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
                            {meta.emailSuggestionsDescription || 'Select an email address for'}{' '}
                            <span className="font-medium text-stone-800 dark:text-stone-200">
                                {selectedRecipient.volunteer
                                    ? `${selectedRecipient.volunteer.first_name} ${selectedRecipient.volunteer.last_name}`
                                    : selectedRecipient.volunteer_id}
                            </span>
                        </p>

                        {loadingEmailSuggestions ? (
                            <div className="py-8 text-center text-stone-500 dark:text-stone-400">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                                <p className="mt-2">Loading suggestions...</p>
                            </div>
                        ) : emailSuggestions ? (
                            <div className="space-y-4">
                                {/* Volunteer's own email if updated */}
                                {emailSuggestions.volunteer_email && (
                                    <div>
                                        <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            Volunteer's Profile Email
                                        </h4>
                                        <button
                                            onClick={() => handleSaveRecipientEmail(emailSuggestions.volunteer_email!)}
                                            disabled={savingEmail}
                                            className="w-full text-start p-3 border border-success-300 dark:border-success-700 rounded-lg bg-success-50 dark:bg-success-900/30 hover:bg-success-100 dark:hover:bg-success-900/50 transition-colors"
                                        >
                                            <span className="text-success-700 dark:text-success-300">
                                                {emailSuggestions.volunteer_email}
                                            </span>
                                            <span className="text-xs text-success-600 dark:text-success-400 ms-2">
                                                (Profile has been updated)
                                            </span>
                                        </button>
                                    </div>
                                )}

                                {/* Family member emails */}
                                {emailSuggestions.family_emails.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            Family Members
                                        </h4>
                                        <div className="space-y-2">
                                            {emailSuggestions.family_emails.map((family, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleSaveRecipientEmail(family.email)}
                                                    disabled={savingEmail}
                                                    className="w-full text-start p-3 border border-stone-300 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                                                >
                                                    <span className="font-medium text-stone-800 dark:text-stone-200">
                                                        {family.name}
                                                    </span>
                                                    <span className="block text-sm text-stone-600 dark:text-stone-400">
                                                        {family.email}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Previously used emails */}
                                {emailSuggestions.previous_emails.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            Previously Used Emails
                                        </h4>
                                        <div className="space-y-2">
                                            {emailSuggestions.previous_emails.map((email, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleSaveRecipientEmail(email)}
                                                    disabled={savingEmail}
                                                    className="w-full text-start p-3 border border-stone-300 dark:border-stone-600 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                                                >
                                                    <span className="text-stone-800 dark:text-stone-200">
                                                        {email}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Custom email input */}
                                <div>
                                    <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                        {meta.customEmailLabel || 'Enter Custom Email'}
                                    </h4>
                                    <div className="flex gap-2">
                                        <input
                                            type="email"
                                            value={customEmail}
                                            onChange={(e) => setCustomEmail(e.target.value)}
                                            placeholder="email@example.com"
                                            className="flex-1 px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-lg text-stone-800 dark:text-stone-200 bg-white dark:bg-stone-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        />
                                        <Button
                                            onClick={() => handleSaveRecipientEmail(customEmail)}
                                            disabled={!customEmail || savingEmail}
                                            variant="solid"
                                            color="primary"
                                        >
                                            {savingEmail ? 'Saving...' : (meta.saveButton || 'Save')}
                                        </Button>
                                    </div>
                                </div>

                                {/* No suggestions message */}
                                {!emailSuggestions.volunteer_email &&
                                    emailSuggestions.family_emails.length === 0 &&
                                    emailSuggestions.previous_emails.length === 0 && (
                                        <div className="py-4 text-center text-stone-500 dark:text-stone-400">
                                            <p>{meta.noEmailSuggestions || 'No suggestions available. Please enter an email manually.'}</p>
                                        </div>
                                    )}
                            </div>
                        ) : (
                            <div className="py-4 text-center text-stone-500 dark:text-stone-400">
                                <p>Unable to load suggestions</p>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        )}

        {/* Targeting Modal */}
        {showTargetingModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                            {meta.targetingTitle}
                        </h3>
                        <p className="text-sm text-stone-600 dark:text-stone-400 mb-6">
                            {meta.targetingDescription}
                        </p>

                        <div className="space-y-4 mb-6">
                            {/* Targeting Method Selection */}
                            <div>
                                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-3">
                                    Select Recipients
                                </label>
                                <div className="space-y-2">
                                    <label className="flex items-start p-3 border border-stone-300 dark:border-stone-600 rounded-md hover:bg-stone-50 dark:hover:bg-stone-800 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="targeting-method"
                                            value="all"
                                            checked={targetingMethod === 'all'}
                                            onChange={() => {
                                                setTargetingMethod('all')
                                                setSelectedEventId(null)
                                                setSelectedAreaId(null)
                                                setSelectedShiftId(null)
                                                setSelectedDutyId(null)
                                                setIncludeEventLeads(false)
                                                setIncludeAreaLeads(false)
                                                setIncludeShiftLeads(false)
                                            }}
                                            className="mt-0.5 me-3 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div>
                                            <div className="font-medium text-stone-900 dark:text-stone-100">
                                                All Registered Volunteers
                                            </div>
                                            <div className="text-sm text-stone-600 dark:text-stone-400">
                                                Send to all volunteers who have submitted their profile, regardless of shift assignments
                                            </div>
                                        </div>
                                    </label>

                                    <label className="flex items-start p-3 border border-stone-300 dark:border-stone-600 rounded-md hover:bg-stone-50 dark:hover:bg-stone-800 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="targeting-method"
                                            value="event"
                                            checked={targetingMethod === 'event'}
                                            onChange={() => {
                                                setTargetingMethod('event')
                                                setSelectedDutyId(null)
                                                setIncludeEventLeads(false)
                                                setIncludeAreaLeads(false)
                                                setIncludeShiftLeads(false)
                                            }}
                                            className="mt-0.5 me-3 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div>
                                            <div className="font-medium text-stone-900 dark:text-stone-100">
                                                Target by Event/Area/Shift
                                            </div>
                                            <div className="text-sm text-stone-600 dark:text-stone-400">
                                                Send to volunteers allocated to specific events, areas, or shifts
                                            </div>
                                        </div>
                                    </label>

                                    <label className="flex items-start p-3 border border-stone-300 dark:border-stone-600 rounded-md hover:bg-stone-50 dark:hover:bg-stone-800 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="targeting-method"
                                            value="duty"
                                            checked={targetingMethod === 'duty'}
                                            onChange={() => {
                                                setTargetingMethod('duty')
                                                setSelectedEventId(null)
                                                setSelectedAreaId(null)
                                                setSelectedShiftId(null)
                                                setIncludeEventLeads(false)
                                                setIncludeAreaLeads(false)
                                                setIncludeShiftLeads(false)
                                            }}
                                            className="mt-0.5 me-3 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div>
                                            <div className="font-medium text-stone-900 dark:text-stone-100">
                                                Target by Duty Type
                                            </div>
                                            <div className="text-sm text-stone-600 dark:text-stone-400">
                                                Send to all volunteers allocated to a specific duty (e.g., Security, Parking)
                                            </div>
                                        </div>
                                    </label>

                                    <label className="flex items-start p-3 border border-stone-300 dark:border-stone-600 rounded-md hover:bg-stone-50 dark:hover:bg-stone-800 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="targeting-method"
                                            value="leads"
                                            checked={targetingMethod === 'leads'}
                                            onChange={() => {
                                                setTargetingMethod('leads')
                                                setSelectedEventId(null)
                                                setSelectedAreaId(null)
                                                setSelectedShiftId(null)
                                                setSelectedDutyId(null)
                                            }}
                                            className="mt-0.5 me-3 text-primary-600 focus:ring-primary-500"
                                        />
                                        <div>
                                            <div className="font-medium text-stone-900 dark:text-stone-100">
                                                Target Leads Only
                                            </div>
                                            <div className="text-sm text-stone-600 dark:text-stone-400">
                                                Send to event leads, area leads/deputies, or shift leads/deputies
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* All Volunteers Filter Options */}
                            {targetingMethod === 'all' && (
                                <div className="space-y-4 border-t border-stone-200 dark:border-stone-700 pt-4 mt-2">
                                    <p className="text-xs text-stone-600 dark:text-stone-400 mb-3">
                                        Optionally filter the registered volunteers by type and volunteering preference.
                                    </p>

                                    {/* Volunteer Types Filter (multi-select) */}
                                    <div>
                                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            {meta.volunteerTypesLabel}
                                        </label>
                                        <div className="space-y-2">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={volunteerTypes.includes('iiuk_uk_ireland')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setVolunteerTypes([...volunteerTypes, 'iiuk_uk_ireland'])
                                                        } else {
                                                            setVolunteerTypes(volunteerTypes.filter(t => t !== 'iiuk_uk_ireland'))
                                                        }
                                                    }}
                                                    className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerTypeIiukUkIreland}
                                                </span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={volunteerTypes.includes('iiuk_europe')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setVolunteerTypes([...volunteerTypes, 'iiuk_europe'])
                                                        } else {
                                                            setVolunteerTypes(volunteerTypes.filter(t => t !== 'iiuk_europe'))
                                                        }
                                                    }}
                                                    className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerTypeIiukEurope}
                                                </span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={volunteerTypes.includes('non_iiuk')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setVolunteerTypes([...volunteerTypes, 'non_iiuk'])
                                                        } else {
                                                            setVolunteerTypes(volunteerTypes.filter(t => t !== 'non_iiuk'))
                                                        }
                                                    }}
                                                    className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerTypeNonIiuk}
                                                </span>
                                            </label>
                                        </div>
                                        {volunteerTypes.length === 0 && (
                                            <p className="mt-1 text-xs text-warn-600 dark:text-warn-400">
                                                Please select at least one volunteer type
                                            </p>
                                        )}
                                    </div>

                                    {/* Volunteer for Visit Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            {meta.volunteerForVisitLabel}
                                        </label>
                                        <div className="space-y-2">
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="volunteer-for-visit"
                                                    value="both"
                                                    checked={volunteerForVisit === 'both'}
                                                    onChange={() => setVolunteerForVisit('both')}
                                                    className="me-2 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerForVisitBoth}
                                                </span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="volunteer-for-visit"
                                                    value="yes"
                                                    checked={volunteerForVisit === 'yes'}
                                                    onChange={() => setVolunteerForVisit('yes')}
                                                    className="me-2 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerForVisitYes}
                                                </span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="volunteer-for-visit"
                                                    value="no"
                                                    checked={volunteerForVisit === 'no'}
                                                    onChange={() => setVolunteerForVisit('no')}
                                                    className="me-2 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.volunteerForVisitNo}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Event/Area/Shift Targeting */}
                            {targetingMethod === 'event' && (
                                <>
                                    <div>
                                        <label htmlFor="event-select" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            {meta.targetEventLabel}
                                        </label>
                                        <select
                                            id="event-select"
                                            value={selectedEventId || ''}
                                            onChange={(e) => {
                                                const value = e.target.value ? parseInt(e.target.value) : null
                                                setSelectedEventId(value)
                                                setSelectedAreaId(null)
                                                setSelectedShiftId(null)
                                            }}
                                            className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100"
                                        >
                                            <option value="">All Events (any allocation)</option>
                                            {loadingEvents ? (
                                                <option disabled>{meta.loadingEvents}</option>
                                            ) : (
                                                sortByName(events).map(event => (
                                                    <option key={event.id} value={event.id}>
                                                        {event.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                                            Select "All Events" to target volunteers with at least one shift allocation
                                        </p>
                                    </div>

                                    {selectedEventId && (
                                        <div>
                                            <label htmlFor="area-select" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                                {meta.targetAreaLabel}
                                            </label>
                                            <select
                                                id="area-select"
                                                value={selectedAreaId || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value ? parseInt(e.target.value) : null
                                                    setSelectedAreaId(value)
                                                    setSelectedShiftId(null)
                                                }}
                                                className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100"
                                            >
                                                <option value="">{meta.selectAreaPlaceholder}</option>
                                                {loadingAreas ? (
                                                    <option disabled>{meta.loadingAreas}</option>
                                                ) : (
                                                    sortByName(areas).map(area => (
                                                        <option key={area.id} value={area.id}>
                                                            {area.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    )}

                                    {selectedAreaId && (
                                        <div>
                                            <label htmlFor="shift-select" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                                {meta.targetShiftLabel}
                                            </label>
                                            <select
                                                id="shift-select"
                                                value={selectedShiftId || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value ? parseInt(e.target.value) : null
                                                    setSelectedShiftId(value)
                                                }}
                                                className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100"
                                            >
                                                <option value="">{meta.selectShiftPlaceholder}</option>
                                                {loadingShifts ? (
                                                    <option disabled>{meta.loadingShifts}</option>
                                                ) : (
                                                    sortShifts(shifts).map(shift => (
                                                        <option key={shift.id} value={shift.id}>
                                                            {getShiftDisplayName(shift)} ({shift.start_time} - {shift.end_time})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Lead Inclusion for Event Targeting */}
                            {targetingMethod === 'event' && (
                                <div className="border-t border-stone-200 dark:border-stone-700 pt-4 mt-2">
                                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                        Include Leads
                                    </label>
                                    <p className="text-xs text-stone-600 dark:text-stone-400 mb-3">
                                        Optionally include leads in addition to allocated volunteers. Leads will be scoped to your selection above.
                                    </p>
                                    <div className="space-y-2 ps-4 border-s-2 border-stone-200 dark:border-stone-700">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeEventLeads}
                                                onChange={(e) => setIncludeEventLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeEventLeadsLabel}
                                            </span>
                                        </label>

                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeAreaLeads}
                                                onChange={(e) => setIncludeAreaLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeAreaLeadsLabel}
                                            </span>
                                        </label>

                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeShiftLeads}
                                                onChange={(e) => setIncludeShiftLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeShiftLeadsLabel}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Duty Targeting */}
                            {targetingMethod === 'duty' && (
                                <>
                                    <div>
                                        <label htmlFor="duty-select" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            {meta.targetDutyLabel}
                                        </label>
                                        <select
                                            id="duty-select"
                                            value={selectedDutyId || ''}
                                            onChange={(e) => {
                                                const value = e.target.value ? parseInt(e.target.value) : null
                                                setSelectedDutyId(value)
                                            }}
                                            className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100"
                                        >
                                            <option value="">{meta.selectDutyPlaceholder}</option>
                                            {loadingDuties ? (
                                                <option disabled>{meta.loadingDuties}</option>
                                            ) : (
                                                duties.map(duty => (
                                                    <option key={duty.id} value={duty.id}>
                                                        {duty.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>

                                    {/* Lead Inclusion for Duty Targeting */}
                                    <div className="border-t border-stone-200 dark:border-stone-700 pt-4 mt-2">
                                        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                                            Include Leads
                                        </label>
                                        <p className="text-xs text-stone-600 dark:text-stone-400 mb-3">
                                            Optionally include shift leads/deputies for all shifts of the selected duty type.
                                        </p>
                                        <div className="space-y-2 ps-4 border-s-2 border-stone-200 dark:border-stone-700">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={includeShiftLeads}
                                                    onChange={(e) => setIncludeShiftLeads(e.target.checked)}
                                                    className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-stone-700 dark:text-stone-300">
                                                    {meta.includeShiftLeadsLabel}
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Lead Targeting */}
                            {targetingMethod === 'leads' && (
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-3">
                                        Select Lead Types
                                    </label>
                                    <div className="space-y-2 ps-4 border-s-2 border-primary-300 dark:border-primary-700">
                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeEventLeads}
                                                onChange={(e) => setIncludeEventLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeEventLeadsLabel}
                                            </span>
                                        </label>

                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeAreaLeads}
                                                onChange={(e) => setIncludeAreaLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeAreaLeadsLabel}
                                            </span>
                                        </label>

                                        <label className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={includeShiftLeads}
                                                onChange={(e) => setIncludeShiftLeads(e.target.checked)}
                                                className="me-2 rounded border-stone-300 dark:border-stone-600 text-primary-600 focus:ring-primary-500"
                                            />
                                            <span className="text-sm text-stone-700 dark:text-stone-300">
                                                {meta.includeShiftLeadsLabel}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Summary message */}
                            <div className="p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-md">
                                <p className="text-sm text-primary-800 dark:text-primary-200">
                                    <strong>Recipients: </strong>
                                    {(() => {
                                        const parts = []

                                        if (targetingMethod === 'all') {
                                            // Build description based on filters
                                            const filterParts = []

                                            // Volunteer types
                                            if (volunteerTypes.length === 3) {
                                                filterParts.push('all registered volunteers')
                                            } else if (volunteerTypes.length > 0) {
                                                const typeLabels = volunteerTypes.map(t => {
                                                    if (t === 'iiuk_uk_ireland') return 'IIUK Local'
                                                    if (t === 'iiuk_europe') return 'IIUK International'
                                                    if (t === 'non_iiuk') return 'Volunteer Community'
                                                    return t
                                                })
                                                filterParts.push(typeLabels.join(', '))
                                            } else {
                                                filterParts.push('no volunteer types selected')
                                            }

                                            // Volunteer for visit
                                            if (volunteerForVisit === 'yes') {
                                                filterParts.push('who want to volunteer during didar')
                                            } else if (volunteerForVisit === 'no') {
                                                filterParts.push('who do not want to volunteer during didar')
                                            }

                                            // Combine parts
                                            let description = filterParts[0].charAt(0).toUpperCase() + filterParts[0].slice(1)
                                            if (filterParts.length > 1) {
                                                description += ' ' + filterParts[1]
                                            }
                                            description += ' (regardless of shift assignments)'
                                            return description
                                        } else if (targetingMethod === 'event') {
                                            // Base targeting
                                            if (selectedShiftId) {
                                                parts.push('Volunteers in selected shift')
                                            } else if (selectedAreaId) {
                                                const areaName = areas.find(a => a.id === selectedAreaId)?.name
                                                parts.push(`All volunteers in ${areaName}`)
                                            } else if (selectedEventId) {
                                                const eventName = events.find(e => e.id === selectedEventId)?.name
                                                parts.push(`All volunteers in ${eventName}`)
                                            } else {
                                                parts.push('All volunteers with at least one shift allocation')
                                            }

                                            // Add lead inclusions if any are selected
                                            const leadParts = []
                                            if (includeEventLeads) leadParts.push('event leads')
                                            if (includeAreaLeads) leadParts.push('area leads/deputies')
                                            if (includeShiftLeads) leadParts.push('shift leads/deputies')

                                            if (leadParts.length > 0) {
                                                parts.push(`plus ${leadParts.join(', ')}`)
                                            }

                                            return parts.join(', ')
                                        } else if (targetingMethod === 'duty') {
                                            if (selectedDutyId) {
                                                const dutyName = duties.find(d => d.id === selectedDutyId)?.name
                                                const parts = [`Volunteers allocated to ${dutyName} shifts`]

                                                // Add lead inclusion if selected
                                                if (includeShiftLeads) {
                                                    parts.push('plus shift leads/deputies')
                                                }

                                                return parts.join(', ')
                                            } else {
                                                return 'Select a duty type'
                                            }
                                        } else if (targetingMethod === 'leads') {
                                            const leadParts = []
                                            if (includeEventLeads) leadParts.push('Event Leads')
                                            if (includeAreaLeads) leadParts.push('Area Leads/Deputies')
                                            if (includeShiftLeads) leadParts.push('Shift Leads/Deputies')

                                            if (leadParts.length === 0) {
                                                return 'Select at least one lead type'
                                            }
                                            return leadParts.join(', ')
                                        }
                                        return ''
                                    })()}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => setShowTargetingModal(false)}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handlePreviewGroupTargeting}
                                disabled={loadingPreviewData}
                                variant="solid"
                                color="primary"
                            >
                                <PlusIcon className="w-5 h-5" />
                                {loadingPreviewData ? 'Loading...' : meta.addGroupButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Preview Confirmation Modal */}
        {showPreviewConfirmModal && previewData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-4">
                            Confirm Add Recipients
                        </h3>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between items-center py-2 border-b border-stone-200 dark:border-stone-700">
                                <span className="text-stone-600 dark:text-stone-400">New recipients to add:</span>
                                <span className="font-semibold text-stone-900 dark:text-stone-100 text-lg">
                                    {previewData.would_add}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-stone-200 dark:border-stone-700">
                                <span className="text-stone-600 dark:text-stone-400">Already in list (duplicates):</span>
                                <span className="font-medium text-stone-500 dark:text-stone-400">
                                    {previewData.duplicates}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-stone-200 dark:border-stone-700">
                                <span className="text-stone-600 dark:text-stone-400">Current total:</span>
                                <span className="font-medium text-stone-700 dark:text-stone-300">
                                    {previewData.current_total}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2 bg-primary-50 dark:bg-primary-900/20 -mx-2 px-2 rounded">
                                <span className="font-medium text-primary-800 dark:text-primary-200">New total after adding:</span>
                                <span className="font-bold text-primary-900 dark:text-primary-100 text-lg">
                                    {previewData.new_total}
                                </span>
                            </div>
                        </div>

                        {previewData.would_add === 0 && (
                            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                                <p className="text-sm text-amber-800 dark:text-amber-200">
                                    No new recipients would be added. All matching volunteers are already in the list.
                                </p>
                            </div>
                        )}

                        {previewData.without_email && previewData.without_email > 0 && (
                            <div className="mb-4 p-3 bg-warn-50 dark:bg-warn-900/20 border border-warn-200 dark:border-warn-800 rounded-md">
                                <p className="text-sm text-warn-800 dark:text-warn-200 flex items-center gap-2">
                                    <span className="text-warn-500">⚠</span>
                                    <span>
                                        <strong>{previewData.without_email}</strong> recipient{previewData.without_email !== 1 ? 's' : ''} {previewData.without_email !== 1 ? 'are' : 'is'} missing email address{previewData.without_email !== 1 ? 'es' : ''}.
                                        You'll need to add emails before sending.
                                    </span>
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => {
                                    setShowPreviewConfirmModal(false)
                                    setPreviewData(null)
                                    setPendingTargetingBody(null)
                                }}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowPreviewConfirmModal(false)
                                    setShowTargetingModal(true)
                                }}
                                variant="outline"
                                color="primary"
                            >
                                Go Back
                            </Button>
                            <Button
                                onClick={handleConfirmAddGroup}
                                disabled={addingRecipients || previewData.would_add === 0}
                                variant="solid"
                                color="primary"
                            >
                                {addingRecipients ? 'Adding...' : `Add ${previewData.would_add} Recipients`}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Send Campaign Confirmation Modal */}
        {showSendModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                            {campaign?.status === 'pending_approval' ? meta.confirmApproveTitle : meta.confirmSubmitTitle}
                        </h3>
                        <p className="text-stone-600 dark:text-stone-400 mb-4">
                            {campaign?.status === 'pending_approval' ? meta.confirmApproveMessage(recipientCount) : meta.confirmSubmitMessage(recipientCount)}
                        </p>
                        {/* Warning about recipients without emails */}
                        {(recipientTotal - recipientWithEmailCount) > 0 && (
                            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                    <strong>{recipientTotal - recipientWithEmailCount}</strong> recipient{(recipientTotal - recipientWithEmailCount) !== 1 ? 's' : ''} without email{(recipientTotal - recipientWithEmailCount) !== 1 ? 's' : ''} will be skipped.
                                    You can add their emails and continue sending later.
                                </p>
                            </div>
                        )}
                        {!campaign?.test_sent_at && (
                            <div className="mb-4 p-3 bg-warn-50 dark:bg-warn-900/20 border border-warn-200 dark:border-warn-800 rounded-md">
                                <p className="text-sm text-warn-800 dark:text-warn-200">
                                    {meta.noTestSentWarning}
                                </p>
                            </div>
                        )}
                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => setShowSendModal(false)}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleConfirmSend}
                                disabled={sendingCampaign}
                                variant="solid"
                                color="primary"
                            >
                                {sendingCampaign ? 'Working...' : meta.confirmButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Deny Campaign Confirmation Modal */}
        {showDenyModal && campaign && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                            {meta.confirmDenyTitle}
                        </h3>
                        <p className="text-stone-600 dark:text-stone-400 mb-6">
                            {meta.confirmDenyMessage}
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => setShowDenyModal(false)}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleConfirmDeny}
                                disabled={denyingCampaign}
                                variant="solid"
                                color="danger"
                            >
                                {denyingCampaign ? 'Working...' : meta.confirmDenyButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Retry Failed Confirmation Modal */}
        {showRetryModal && campaign && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full">
                    <div className="p-6">
                        <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                            {meta.retryFailedConfirmTitle}
                        </h3>
                        <p className="text-stone-600 dark:text-stone-400 mb-4">
                            {meta.retryFailedConfirmMessage(campaign.failed_count)}
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={() => setShowRetryModal(false)}
                                variant="outline"
                                color="neutral"
                            >
                                {meta.cancelButton}
                            </Button>
                            <Button
                                onClick={handleRetryFailed}
                                disabled={retryingFailed}
                                variant="solid"
                                color="warn"
                            >
                                {retryingFailed ? 'Retrying...' : meta.confirmRetryButton}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        )}

        {/* Custom Styles for Quill Editor */}
        <style>{`
            .email-editor .ql-container {
                min-height: 300px;
                font-size: 16px;
            }
            .email-editor .ql-editor {
                min-height: 300px;
            }
            .email-editor .ql-toolbar {
                border-top-left-radius: 0.5rem;
                border-top-right-radius: 0.5rem;
            }
            .email-editor .ql-container {
                border-bottom-left-radius: 0.5rem;
                border-bottom-right-radius: 0.5rem;
            }

            /* Dark mode support */
            .dark .email-editor .ql-toolbar {
                background-color: #292524;
                border-color: #57534e;
            }
            .dark .email-editor .ql-container {
                background-color: #292524;
                border-color: #57534e;
            }
            .dark .email-editor .ql-editor {
                color: #f5f5f4;
            }
            .dark .email-editor .ql-editor.ql-blank::before {
                color: #78716c;
            }
            .dark .email-editor .ql-stroke {
                stroke: #d6d3d1;
            }
            .dark .email-editor .ql-fill {
                fill: #d6d3d1;
            }
            .dark .email-editor .ql-picker-label {
                color: #d6d3d1;
            }
            .dark .email-editor .ql-picker-options {
                background-color: #292524;
                border-color: #57534e;
            }
            .dark .email-editor .ql-picker-item {
                color: #d6d3d1;
            }
            .dark .email-editor .ql-picker-item:hover {
                background-color: #44403c;
            }
        `}</style>

        {/* Preview Modal */}
        {showPreviewModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                <div className="bg-white dark:bg-stone-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden relative flex flex-col">
                    {/* Close button */}
                    <div className="p-4 border-b border-stone-200 dark:border-stone-700 flex justify-between items-center sticky top-0 bg-white dark:bg-stone-900 z-10">
                        <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Email Preview</h3>
                        <Button
                            onClick={() => setShowPreviewModal(false)}
                            variant="solid"
                            color="neutral"
                        >
                            Close
                        </Button>
                    </div>

                    {/* Email Preview Content - rendered from backend template */}
                    <div className="flex-1 overflow-y-auto">
                        <iframe
                            srcDoc={previewHtml}
                            title="Email Preview"
                            className="w-full h-full min-h-[600px] border-0"
                            sandbox="allow-same-origin"
                        />
                    </div>
                </div>
            </div>
        )}
    </div>
    )
}

export default () => <EmailCampaignForm meta={EmailCampaignFormMeta} />
